<script>
import { GlButton, GlDisclosureDropdown, GlTooltipDirective } from '@gitlab/ui';
import { MountingPortal } from 'portal-vue';
import { s__ } from '~/locale';
import DuoWorkItemToMrAction from 'ee/ai/shared/widgets/duo_work_item_to_mr_action.vue';
import WorkPlanView from './work_plan_view.vue';
import WorkPlanEditor from './work_plan_editor.vue';
import { WORKPLAN_GOAL_PREFIX, GENERATE_MR_BUTTON_OPTIONS } from './constants';

export default {
  name: 'WorkPlanPanel',
  directives: {
    GlTooltip: GlTooltipDirective,
  },
  components: {
    GlButton,
    GlDisclosureDropdown,
    MountingPortal,
    DuoWorkItemToMrAction,
    WorkPlanView,
    WorkPlanEditor,
  },
  inject: ['fullPath'],
  inheritAttrs: false,
  props: {
    open: {
      type: Boolean,
      required: true,
    },
    workItemId: {
      type: String,
      required: true,
    },
    canUpdate: {
      type: Boolean,
      required: true,
    },
    savedContent: {
      type: String,
      required: true,
    },
    draftContent: {
      type: String,
      required: true,
    },
    isEditing: {
      type: Boolean,
      required: true,
    },
    isSaving: {
      type: Boolean,
      required: true,
    },
    isLoading: {
      type: Boolean,
      required: true,
    },
    workItemIid: {
      type: [String, Number],
      required: true,
    },
    workItemType: {
      type: String,
      required: true,
    },
    workItemWebUrl: {
      type: String,
      required: true,
    },
    hasRemoteFlowsEnabled: {
      type: Boolean,
      required: true,
    },
  },
  emits: ['close', 'save', 'delete', 'draft-change', 'start-edit', 'cancel-edit', 'regenerate'],
  computed: {
    isEditingAllowed() {
      return this.canUpdate && !this.isEditing;
    },
    hasSavedContent() {
      return Boolean(this.savedContent);
    },
    showGenerateMrAction() {
      return this.hasRemoteFlowsEnabled && !this.isEditing && this.hasSavedContent;
    },
    panelHeading() {
      return s__('AgentPlan|Workplan');
    },
    editMoreActions() {
      const actions = [];
      if (this.hasSavedContent) {
        actions.push({
          text: s__('AgentPlan|Regenerate workplan'),
          icon: 'tanuki-ai',
          action: () => this.$emit('regenerate'),
        });
        actions.push({
          text: s__('AgentPlan|Delete workplan'),
          icon: 'remove',
          action: () => this.$emit('delete'),
          variant: 'danger',
        });
      }
      return actions;
    },
  },
  mounted() {
    document.addEventListener('keydown', this.handleKeydown);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeydown);
  },
  methods: {
    handleClose() {
      const active = document.activeElement;
      const activeTag = active?.tagName;
      if (
        document.body.classList.contains('modal-open') ||
        active?.closest('.js-editor') != null ||
        activeTag === 'INPUT' ||
        activeTag === 'TEXTAREA'
      ) {
        return;
      }
      this.$emit('close');
    },
    handleKeydown({ key }) {
      if (key === 'Escape' && this.open) {
        this.handleClose();
      }
    },
  },
  WORKPLAN_GOAL_PREFIX,
  generateMrButtonOptions: { ...GENERATE_MR_BUTTON_OPTIONS, size: 'small' },
};
</script>

<template>
  <mounting-portal v-if="open" mount-to="#contextual-panel-portal" append>
    <div data-testid="work-plan-panel" class="work-item-detail-panel gl-leading-reset">
      <div class="work-item-detail-panel-header">
        <div class="gl-flex gl-h-full gl-min-w-0 gl-grow gl-items-center gl-gap-2">
          <span
            class="gl-truncate gl-text-sm gl-font-bold gl-text-default"
            data-testid="work-plan-panel-title"
          >
            {{ panelHeading }}
          </span>
        </div>
        <duo-work-item-to-mr-action
          v-if="showGenerateMrAction"
          run-duo-developer-in-chat
          :project-path="fullPath"
          :work-item-iid="workItemIid"
          :work-item-type="workItemType"
          :work-item-web-url="workItemWebUrl"
          :additional-goal-context="$options.WORKPLAN_GOAL_PREFIX"
          :generate-mr-button-options="$options.generateMrButtonOptions"
          data-testid="panel-generate-mr-with-duo"
        />
        <template v-if="isEditingAllowed">
          <gl-button
            size="small"
            category="tertiary"
            data-testid="panel-edit-button"
            @click="$emit('start-edit')"
          >
            {{ s__('AgentPlan|Edit') }}
          </gl-button>
          <gl-disclosure-dropdown
            v-if="editMoreActions.length"
            v-gl-tooltip.bottom
            size="small"
            category="tertiary"
            icon="ellipsis_h"
            no-caret
            text-sr-only
            :toggle-text="__('More actions')"
            :title="__('More actions')"
            :items="editMoreActions"
            data-testid="panel-edit-more-actions"
          />
        </template>
        <gl-button
          v-gl-tooltip.bottom
          class="gl-detail-panel-close-button"
          data-testid="panel-close-button"
          category="tertiary"
          icon="close"
          size="small"
          :aria-label="__('Close panel')"
          :title="__('Close panel')"
          @click="handleClose"
        />
      </div>
      <work-plan-editor
        v-if="isEditing"
        :saved-content="savedContent"
        :draft-content="draftContent"
        :is-saving="isSaving"
        @save="$emit('save', $event)"
        @cancel-edit="$emit('cancel-edit')"
        @draft-change="$emit('draft-change', $event)"
      />
      <work-plan-view
        v-else
        :is-loading="isLoading"
        :can-update="canUpdate"
        :saved-content="savedContent"
        :work-item-id="workItemId"
        :work-item-web-url="workItemWebUrl"
        @start-edit="$emit('start-edit')"
      />
    </div>
  </mounting-portal>
</template>
